/**
 * Step2Client — Sélection du client cible + template optionnel.
 *
 * Props :
 *   - state (contient source_supplier_name, source_invoice_number, source_total_ttc, lines)
 *   - selectedClient, setSelectedClient
 *   - templates, selectedTemplateId, applyingTpl
 *   - onApplyTemplate(tplId), onClearTemplate()
 *   - creatorOpen, setCreatorOpen
 *   - onClientCreated(client)
 *   - onBack(), onContinue()
 */
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileText, ArrowLeft, ArrowRight, Layers, Plus, X, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import ClientCombobox from "@/components/ClientCombobox";
import ClientEditorSheet from "@/components/ClientEditorSheet";
import InvoiceDetailSheet from "@/components/InvoiceDetailSheet";
import { useState } from "react";
import { eur } from "./utils";

export default function Step2Client({
  state,
  selectedClient, setSelectedClient,
  templates, selectedTemplateId, applyingTpl,
  onApplyTemplate, onClearTemplate,
  creatorOpen, setCreatorOpen, onClientCreated,
  onBack, onContinue,
}) {
  const [previewId, setPreviewId] = useState(null);
  const lineCount = (state?.lines || []).length;
  return (
    <div className="space-y-4">
      <Card className="border-slate-200 bg-white rounded-xl p-5">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
          <FileText className="h-3.5 w-3.5 text-violet-600" />
          Source détectée
        </div>
        <div className="text-sm font-medium text-slate-900">
          {state.source_supplier_name || "Fournisseur"} —
          n° {state.source_invoice_number || "—"} — {eur(state.source_total_ttc || 0)} TTC
          <span className="ml-2 text-xs text-slate-500">
            ({lineCount} ligne{lineCount > 1 ? "s" : ""})
          </span>
        </div>
      </Card>

      <Card className="border-slate-200 bg-white rounded-xl p-5 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
            Client à qui refacturer *
          </Label>
          <ClientCombobox
            value={selectedClient}
            onSelect={(c) => setSelectedClient(c)}
            onClear={() => setSelectedClient(null)}
          />
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <span>Client absent ?</span>
            <button
              className="text-violet-700 hover:underline"
              onClick={() => setCreatorOpen(true)}
              data-testid="rebilling-create-client-btn"
            >
              <Plus className="h-3 w-3 inline mr-0.5" /> Créer un client
            </button>
          </div>
        </div>

        {selectedClient && (
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="h-4 w-4 text-slate-500" />
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Template mise en forme (optionnel)
              </Label>
            </div>
            {templates.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                Aucune ancienne facture pour ce client — on partira de zéro.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">
                  Réutiliser la mise en forme d'une ancienne facture émise à ce client :
                </p>
                <div className="grid gap-2 max-h-56 overflow-y-auto">
                  {templates.map((t) => (
                    <label key={t.id}
                      className={cn(
                        "flex items-center gap-3 border rounded-lg p-2.5 cursor-pointer text-sm",
                        selectedTemplateId === t.id
                          ? "border-violet-400 bg-violet-50"
                          : "border-slate-200 hover:border-slate-300"
                      )}
                      data-testid={`rebilling-tpl-${t.id}`}
                    >
                      <input id={`rebilling-tpl-radio-${t.id}`} name="rebilling-tpl-radio" type="radio" checked={selectedTemplateId === t.id}
                              onChange={() => onApplyTemplate(t.id)}
                              disabled={applyingTpl} className="accent-violet-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">
                          {t.number || "sans n°"}
                          {t.has_rebilling_history && (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px] border border-emerald-200">
                              Refacturation
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {(t.issued_at || "").slice(0, 10)} · {t.line_count} ligne(s) · {eur(t.total_ttc || 0)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewId(t.id); }}
                        className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-violet-700 border border-transparent hover:border-slate-200"
                        title="Prévisualiser cette facture"
                        aria-label="Prévisualiser"
                        data-testid={`rebilling-tpl-preview-${t.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </label>
                  ))}
                </div>
                {selectedTemplateId && (
                  <button className="text-xs text-slate-500 hover:text-red-700 hover:underline"
                          onClick={onClearTemplate}>
                    <X className="h-3 w-3 inline mr-0.5" /> Retirer le template
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between pt-3 border-t border-slate-100">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Changer de source
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white"
            disabled={!selectedClient}
            onClick={onContinue}
            data-testid="rebilling-goto-step3"
          >
            Continuer <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </Card>

      <ClientEditorSheet
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        onSaved={onClientCreated}
      />

      <InvoiceDetailSheet
        invoiceId={previewId}
        open={!!previewId}
        onOpenChange={(v) => { if (!v) setPreviewId(null); }}
      />
    </div>
  );
}
